/**
 * Proteção contra perda de trabalho não salvo. (BKC-008)
 *
 * O ciclo de teste registrou o caso concreto: preencher um formulário longo,
 * clicar fora por engano e perder tudo, sem aviso. Não é um problema de
 * conveniência — é a diferença entre a pessoa confiar no sistema para digitar
 * dados de reunião e preferir anotar no papel primeiro.
 *
 * São TRÊS saídas possíveis, e cada uma precisa de um mecanismo diferente:
 *
 *   1. Fechar o modal (clique fora, Escape, botão de fechar) — tratado dentro do
 *      próprio `Modal`, com confirmação na interface.
 *   2. Navegar para outra rota — `useNavigationGuard`, via `useBlocker` do
 *      React Router.
 *   3. Fechar a aba ou recarregar a página — `useBeforeUnload`, que é a ÚNICA
 *      coisa que o navegador aceita interceptar aí, e apenas com o diálogo
 *      nativo (o texto é escolhido pelo navegador; não há como personalizá-lo
 *      desde 2017, e tentar seria código morto).
 *
 * O QUE NÃO SE FAZ AQUI: salvar rascunho automaticamente. Gravar dado parcial no
 * servidor criaria registro incompleto que alguém encontraria depois sem saber o
 * que é — e um rascunho no navegador sobreviveria a um logout, o que é pior num
 * sistema com dado pessoal.
 */

import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Avisa antes de a aba fechar ou recarregar.
 *
 * O navegador exibe texto próprio e ignora qualquer mensagem que passemos — é uma
 * restrição deliberada dele contra páginas que sequestram a saída.
 */
export function useBeforeUnload(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;

    const handler = (event: BeforeUnloadEvent): void => {
      // `preventDefault` é o que os navegadores modernos exigem; `returnValue`
      // continua necessário para os mais antigos.
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
}

export interface NavigationGuard {
  /** `true` quando uma navegação está retida esperando decisão. */
  blocked: boolean;
  /** Descarta as alterações e segue para onde a pessoa clicou. */
  discard: () => void;
  /** Fica na tela. */
  stay: () => void;
}

/**
 * Retém a navegação interna enquanto houver alteração não salva.
 *
 * Devolve o estado para a tela desenhar a confirmação — em vez de um
 * `window.confirm`, que não é estilizável, não é testável de forma confiável e
 * some do fluxo visual do sistema.
 */
export function useNavigationGuard(dirty: boolean): NavigationGuard {
  const blocker = useBlocker(dirty);

  const discard = useCallback(() => {
    // `proceed` só existe no estado 'blocked'; chamar fora dele é erro de uso.
    if (blocker.state === 'blocked') blocker.proceed();
  }, [blocker]);

  const stay = useCallback(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  useBeforeUnload(dirty);

  return { blocked: blocker.state === 'blocked', discard, stay };
}

/**
 * O formulário mudou em relação ao estado inicial?
 *
 * Comparação por VALOR, não por referência: comparar objetos com `!==` acusaria
 * alteração em todo render e a confirmação apareceria sempre — o que, na prática,
 * treina a pessoa a clicar em "descartar" sem ler.
 *
 * `JSON.stringify` basta porque estes formulários guardam apenas texto, número,
 * booleano e vetor desses tipos. A ordem das chaves é estável porque os dois
 * objetos vêm do mesmo literal.
 */
export function isDirty<T>(atual: T, inicial: T): boolean {
  return JSON.stringify(atual) !== JSON.stringify(inicial);
}
